import React, { useMemo } from 'react';

// components
import AampeTimePicker from '../../../components/inputs/datepicker/AampeTimePicker';
import { SearchableSelectType } from '../../../components/inputs/searchableSelect';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
// constants
import ICONS from '../../../constants/icons';

// helpers
import { getUTCDate } from '../../../helpers/dateTimeHelpers';

import DeliveryTypeInfoSection from './DeliveryTypeInfoSection';
import FormulaDatePicker from './FormulaDatePicker';
import TimeZoneDropdown from './TimeZoneDropdown';

interface StyledProps {
    isDisabled?: boolean;
}
interface ControlledDeliverySectionProps {
    isDisabled?: boolean;
    startDate: Date | null;
    setStartDate: (startDate: Date) => void;
    onTimezoneChanged: (value: SearchableSelectType) => void;
    timezoneValue?: string;
}

const ControlledDeliverySection: React.FC<
    React.PropsWithChildren<ControlledDeliverySectionProps>
> = ({
    isDisabled,
    startDate,
    setStartDate,
    onTimezoneChanged,
    timezoneValue,
}) => {
    const minDate = useMemo<Date>(() => getUTCDate(), []);
    return (
        <Container>
            <DeliveryTypeInfoSection
                title="Send to everyone at once, bypassing the AI"
                description="Select the specific time to send, and all eligible users will receive the message at the same time. This completely bypasses AI optimization, so content, timing, and frequency will not be personalized. Messages may arrive up to 30 minutes later than the scheduled time due to messaging provider queuing."
            />
            <DateTimeContainer>
                <InputSection>
                    <Header>Schedule date</Header>
                    <AampeDatePickerWrapper isDisabled={isDisabled}>
                        <FormulaDatePicker
                            selectedDate={startDate}
                            setSelectedDate={(date: Date) => setStartDate(date)}
                            isDisabled={isDisabled}
                            minDate={minDate}
                            maxDate={null}
                        />
                    </AampeDatePickerWrapper>
                </InputSection>
                <InputSection>
                    <Header>Schedule time</Header>
                    <AampeDatePickerWrapper>
                        <ICONS.clock
                            size={20}
                            color={aampeTheme.colors.neutral09}
                        />
                        <AampeTimePicker
                            selectedDate={startDate}
                            setSelectedDate={(date: Date) => setStartDate(date)}
                            isDisabled={isDisabled || !startDate}
                        />
                    </AampeDatePickerWrapper>
                </InputSection>
                <InputSection>
                    <Header>Choose timezone</Header>
                    <TimeZoneDropdown
                        disabled={isDisabled}
                        onChange={onTimezoneChanged}
                        value={timezoneValue as string}
                    />
                </InputSection>
            </DateTimeContainer>
        </Container>
    );
};
const Container = styled.div({
    display: `flex`,
    alignItems: `start`,
    flexDirection: `column`,
    gap: aampeTheme.spacing(3),
});

const InputSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
    color: aampeTheme.colors.gray800,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
});

const DateTimeContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(3),
    flexWrap: 'wrap',
});

const Header = styled.span({
    color: aampeTheme.colors.gray800,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
});

const AampeDatePickerWrapper = styled.div.withConfig({})<StyledProps>(
    ({ isDisabled }) => ({
        display: 'flex',
        alignItems: 'center',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
    }),
);

export default ControlledDeliverySection;
